import React from 'react';
import { Flex, Spin, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone
} from '@ant-design/icons';
import { ProgressTableRow } from '../types';
import { renderPatientIdentifier } from '../utils/format';

interface PatientListProgressTableProps {
  rows: ProgressTableRow[];
  showStatus?: boolean; // If false, we just show "Pending" or "Skipped"
}

export function PatientListProgressTable(props: PatientListProgressTableProps) {
  const rows = props.rows;
  const showStatus = props.showStatus ?? true;

  return (
    <div
      style={{
        maxHeight: 250,
        overflowY: 'auto',
        border: '1px solid #f0f0f0',
        borderRadius: 4,
        padding: 12
      }}
    >
      <Flex vertical gap="small">
        {rows.map(row => {
          const isSkipped = row.isSkipped;
          const phase_1 = row.phase_1;
          const phase_2 = row.phase_2;
          const errorMsg = row.phase_2_error?.message || row.phase_1_error?.message;

          return (
            <div key={row.id} style={{ padding: '4px 0' }}>
              <Flex justify="space-between" align="center">
                <span style={isSkipped ? { color: '#8c8c8c' } : undefined}>
                  {renderPatientIdentifier(row.rawPatient)}
                  {isSkipped && row.activeStatus && (
                    <span
                      style={{
                        fontSize: '12px',
                        marginLeft: 8,
                        fontStyle: 'italic'
                      }}
                    >
                      (Assessment exists: {row.activeStatus})
                    </span>
                  )}
                </span>

                <Flex gap="small" align="center">
                  {/* Phase 1: Create Assessment */}
                  <Tooltip
                    title={
                      isSkipped
                        ? 'Create Assessment (Skipped)'
                        : 'Create Assessment'
                    }
                  >
                    <div>
                      {isSkipped ? (
                        <CheckCircleTwoTone twoToneColor="#1890ff" />
                      ) : !showStatus ? (
                        <CheckCircleOutlined style={{ color: '#bfbfbf' }} />
                      ) : (
                        <>
                          {phase_1 === null && (
                            <CheckCircleOutlined style={{ color: '#bfbfbf' }} />
                          )}
                          {phase_1 === 'pending' && <Spin size="small" />}
                          {phase_1 === 'error' && (
                            <CloseCircleTwoTone twoToneColor="#ff4d4f" />
                          )}
                          {phase_1 === 'completed' && (
                            <CheckCircleTwoTone twoToneColor="#52c41a" />
                          )}
                        </>
                      )}
                    </div>
                  </Tooltip>

                  {/* Phase 2: Send Email */}
                  <Tooltip title="Send Assessment Email">
                    <div>
                      {!showStatus ? (
                        <CheckCircleOutlined style={{ color: '#bfbfbf' }} />
                      ) : (
                        <>
                          {phase_2 === null && (
                            <CheckCircleOutlined style={{ color: '#bfbfbf' }} />
                          )}
                          {phase_2 === 'pending' && <Spin size="small" />}
                          {phase_2 === 'error' && (
                            <CloseCircleTwoTone twoToneColor="#ff4d4f" />
                          )}
                          {phase_2 === 'completed' && (
                            <CheckCircleTwoTone twoToneColor="#52c41a" />
                          )}
                        </>
                      )}
                    </div>
                  </Tooltip>
                </Flex>
              </Flex>
              {showStatus && errorMsg && (
                <div
                  style={{
                    color: '#ff4d4f',
                    fontSize: '12px',
                    marginTop: 2,
                    paddingLeft: 8
                  }}
                >
                  Error: {errorMsg}
                </div>
              )}
            </div>
          );
        })}
      </Flex>
    </div>
  );
}
